import { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Plus, 
  Edit2, 
  Save,
  X,
  Trash2
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { FetchSubDevicesQuery } from '@/lib/api/devices/devices';
import { 
  CreateSpeakerMut, 
  UpdateSpeakerMut, 
  DeleteSpeakerMut,
  type CreateSpeakerInput,
  type UpdateSpeakerInput 
} from '@/lib/api/monitoring/speakers';
import { DialogDescription } from '@radix-ui/react-dialog';


interface SpeakerComponentsModalProps {
  deviceId: string;
  subDeviceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
}

export const SpeakerComponentsModal = ({
  deviceId,
  subDeviceId,
  open,
  onOpenChange,
  onClose,
}: SpeakerComponentsModalProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);

  const queryClient = useQueryClient();
  
  // Fetch fresh device data
  const { data: subDevices } = useQuery(FetchSubDevicesQuery({ deviceId }));
  const device = subDevices?.find(d => d.id === subDeviceId);
  const speakers = (device as any)?.speakers || [];
  
  // Check if this is an NVR device (max 1 speaker allowed)
  const isNvrCamera = (device as any)?.integrationType?.toLowerCase() === 'nvr' || 
                      (device as any)?.integrationType?.toLowerCase() === 'camera' ||
                      !(device as any)?.integrationType; // Assume cameras if no integrationType
  const maxSpeakers = isNvrCamera ? 1 : Infinity;

  const createSpeakerMut = useMutation({
    ...CreateSpeakerMut(),
    onMutate: async (newSpeaker: CreateSpeakerInput) => {
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((device: any) => {
          if (device.id === subDeviceId) {
            return {
              ...device,
              speakers: [
                ...(device.speakers || []),
                {
                  id: `temp_${Date.now()}`,
                  name: newSpeaker.name,
                  componentGroupId: newSpeaker.componentGroupId
                }
              ]
            };
          }
          return device;
        });
      });

      return { previousData };
    },
    onError: (err, _newSpeaker, context) => {
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to create speaker',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      setIsAdding(false);
      setNewName('');
      toast({
        title: 'Speaker created',
        description: 'Speaker component has been created successfully.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  const updateSpeakerMut = useMutation({
    ...UpdateSpeakerMut(),
    onMutate: async (updatedSpeaker: UpdateSpeakerInput) => {
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((device: any) => {
          if (device.id === subDeviceId) {
            return {
              ...device,
              speakers: (device.speakers || []).map((speaker: any) => 
                speaker.id === updatedSpeaker.id 
                  ? { ...speaker, name: updatedSpeaker.name }
                  : speaker
              )
            };
          }
          return device;
        });
      });

      return { previousData };
    },
    onError: (err, _updatedSpeaker, context) => {
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to update speaker',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      setEditingId(null);
      setEditName('');
      toast({
        title: 'Speaker updated',
        description: 'Speaker component has been updated successfully.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  const deleteSpeakerMut = useMutation({
    ...DeleteSpeakerMut(),
    onMutate: async (deletedSpeaker: { id: string }) => {
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((device: any) => {
          if (device.id === subDeviceId) {
            return {
              ...device,
              speakers: (device.speakers || []).filter((speaker: any) => speaker.id !== deletedSpeaker.id)
            };
          }
          return device;
        });
      });

      return { previousData };
    },
    onError: (err, _deletedSpeaker, context) => {
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to delete speaker',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Speaker deleted',
        description: 'Speaker component has been deleted successfully.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  const startEditing = (speaker: any) => {
    setEditingId(speaker.id);
    setEditName(speaker.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
  };

  const saveEdit = (speakerId: string) => {
    const current = speakers.find((s: any) => s.id === speakerId) || {};
    const input: UpdateSpeakerInput = {
      id: speakerId,
      name: editName.trim(),
      componentGroupId: current.componentGroupId || subDeviceId,
      relatedComponentGroups: current.relatedComponentGroups || [],
    } as UpdateSpeakerInput;
    updateSpeakerMut.mutate(input);
  };

  const startAdding = () => {
    setIsAdding(true);
    setNewName('');
  };

  const cancelAdding = () => {
    setIsAdding(false);
    setNewName('');
  };

  const saveNew = () => {
    if (newName.trim()) {
      const input: CreateSpeakerInput = {
        name: newName.trim(),
        componentGroupId: subDeviceId
      };
      createSpeakerMut.mutate(input);
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      deleteSpeakerMut.mutate({ id: deleteConfirm.id });
      setDeleteConfirm(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Speaker Management - {device?.name || 'Unknown Device'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 px-6">
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-4">
              <p className="text-sm text-gray-300 leading-relaxed">
                Speakers will be automatically populated when supported by your device integration. 
                If your integration doesn't automatically detect speakers, you can manually add them here.
              </p>
            </div>
            
            <div className="max-w-2xl">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {speakers.map((speaker: any) => {
                    const isEditing = editingId === speaker.id;
                    
                    return (
                      <TableRow key={speaker.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {isEditing ? (
                              <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="h-8 w-48"
                                autoFocus
                                onBlur={(e) => e.preventDefault()}
                              />
                            ) : (
                              <span className="font-medium">{speaker.name}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {isEditing ? (
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                onClick={() => saveEdit(speaker.id)}
                                disabled={updateSpeakerMut.isPending}
                                className="bg-lime text-black hover:bg-lime/80"
                              >
                                <Save className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelEditing}
                                className="text-gray-400 hover:text-white"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                onClick={() => startEditing(speaker)}
                                className="bg-lime text-black hover:bg-lime/80"
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeleteConfirm({ id: speaker.id, name: speaker.name })}
                                disabled={deleteSpeakerMut.isPending}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {isAdding && (
                    <TableRow>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Enter speaker name"
                            className="h-8 flex-1"
                            autoFocus
                            onBlur={(e) => e.preventDefault()}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            onClick={saveNew}
                            disabled={createSpeakerMut.isPending}
                            className="bg-lime text-black hover:bg-lime/80"
                          >
                            <Save className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={cancelAdding}
                            className="text-gray-400 hover:text-white"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {!isAdding && speakers.length < maxSpeakers && (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-6">
                        <Button 
                          variant="ghost" 
                          onClick={startAdding}
                          className="text-lime hover:text-lime/80 hover:bg-lime/10"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Speaker Component
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                  {!isAdding && speakers.length >= maxSpeakers && isNvrCamera && (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-6 text-gray-500">
                        <p className="text-sm">Maximum speakers reached for this device (1)</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Speaker</DialogTitle>
            <DialogDescription className="text-gray-400 space-y-2">
              <p>Are you sure you want to delete <span className="font-medium text-white">"{deleteConfirm?.name}"</span>?</p>
              <p>This action cannot be undone.</p>
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
};
